import { Node, mergeAttributes } from "@tiptap/core";
import { TextSelection } from "prosemirror-state";

export const CustomMentionNode = Node.create({
  name: "mention",
  group: "inline",
  inline: true,
  selectable: true,
  atom: true,

  /**
   * Defines the attributes for the mention node
   * @returns {Object} Node attributes configuration
   */
  addAttributes() {
    return {
      id: { default: null },
      label: { default: null },
      position: { default: null },
      isEdited: { default: false },
    };
  },

  /**
   * Defines how to parse HTML into a mention node
   * @returns {Array} Array of parsing rules
   */
  parseHTML() {
    return [
      {
        tag: 'span[data-type="mention"]',
        getAttrs: (element) => {
          // Check for edited state through class or attribute
          const isEdited =
            element.classList.contains("edited") ||
            element.getAttribute("isedited") === "true" ||
            element.getAttribute("data-edited") === "true";

          // Get label from attribute, or fallback to text content
          const label = element.getAttribute("data-label") || element.textContent;

          return {
            id: element.getAttribute("data-id"),
            label: label,
            position: element.getAttribute("data-position"),
            isEdited: isEdited,
          };
        },
      },
    ];
  },

  /**
   * Defines how to render the mention node to HTML
   * @param {Object} params - Rendering parameters
   * @returns {Array} Array of HTML elements
   */
  renderHTML({ node, HTMLAttributes }) {
    const isEdited = node.attrs.isEdited;

    const attrs = mergeAttributes(HTMLAttributes, {
      "data-type": "mention",
      "data-id": node.attrs.id,
      "data-label": node.attrs.label,
      "data-position": node.attrs.position,
      isedited: isEdited ? "true" : "false",
      class: `mention ${isEdited ? "edited" : ""}`,
    });

    return [
      "span",
      attrs,
      [
        "span",
        {
          class: "mention-text",
        },
        node.attrs.label,
      ],
    ];
  },

  addCommands() {
    return {
      setMention:
        (attributes) =>
        ({ commands, state }) => {
          const position = state.selection.from;
          return commands.insertContent({
            type: this.name,
            attrs: { ...attributes, position: position.toString() },
          });
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      Backspace: ({ editor }) => {
        const { $from } = editor.state.selection;
        const node = $from.node();
        if (node.type.name === "mention") {
          const text = node.attrs.label;
          editor.commands.deleteNode();
          editor.commands.insertContent(text);
          return true;
        }
        return false;
      },
    };
  },

  /**
   * Creates a custom node view for the mention
   * @returns {Function} Node view factory
   */
  addNodeView() {
    return ({ node, HTMLAttributes, getPos, editor }) => {
      let currentNode = node;
      const view = editor.view;
      let isEditing = false;
      let inputEl = null;

      // Create the outer wrapper span
      const dom = document.createElement("span");
      dom.className = `mention ${node.attrs.isEdited ? "edited" : ""}`;
      dom.style.position = "relative";
      dom.style.display = "inline-block";
      Object.entries(HTMLAttributes).forEach(([k, v]) =>
        dom.setAttribute(k, v)
      );

      // 2) Visible label
      const textSpan = document.createElement("span");
      textSpan.className = "mention-text";
      textSpan.textContent = currentNode.attrs.label;
      Object.assign(textSpan.style, {
        display: "inline-block",
        padding: "0.2em 0.4em",
        whiteSpace: "nowrap",
        overflow: "hidden",
        boxSizing: "border-box",
        verticalAlign: "middle",
      });
      dom.appendChild(textSpan);

      // Add edit icon
      const editIcon = document.createElement("span");
      editIcon.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="vertical-align: middle; margin-left: 10px; cursor: pointer; display: inline !important;">
          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
        </svg>
      `;
      editIcon.style.display = "inline-block";
      editIcon.addEventListener("click", (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        if (isEditing) {
          inputEl.blur();
        } else {
          startEditing();
        }
      });
      dom.appendChild(editIcon);

      // 3) save & exit
      const saveAndExit = (newLabel) => {
        const pos = getPos();
        if (typeof pos !== "number") return;

        // Only mark as edited if the label actually changed or if it was already edited
        const isLabelChanged = newLabel !== currentNode.attrs.label;
        const shouldBeEdited = isLabelChanged || currentNode.attrs.isEdited;

        const tr = view.state.tr.setNodeMarkup(pos, undefined, {
          ...currentNode.attrs,
          label: newLabel,
          isEdited: shouldBeEdited,
        });
        const end = pos + currentNode.nodeSize;
        const sel = TextSelection.near(tr.doc.resolve(end));
        tr.setSelection(sel).scrollIntoView();
        view.dispatch(tr);

        // swap out classes
        dom.classList.remove("editing");
        if (shouldBeEdited) {
          dom.classList.add("edited");
        }

        isEditing = false;
        if (inputEl && inputEl.parentNode)
          inputEl.parentNode.removeChild(inputEl);
        inputEl = null;
        textSpan.style.visibility = "visible";

        // Switch back to edit icon
        editIcon.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="vertical-align: middle; margin-left: 10px; cursor: pointer; display: inline !important;">
            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
          </svg>
        `;
      };

      // 4) start editing
      const startEditing = () => {
        if (isEditing) return;
        const pos = getPos();
        if (typeof pos !== "number") return;
        isEditing = true;

        // tag as editing
        dom.classList.add("editing");
        dom.classList.remove("edited");

        // Switch to checkmark icon
        editIcon.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="vertical-align: middle; margin-left: 10px; cursor: pointer; display: inline !important;">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
          </svg>
        `;

        // create input
        inputEl = document.createElement("input");
        inputEl.type = "text";
        inputEl.value = currentNode.attrs.label;

        // copy styles
        const cs = window.getComputedStyle(textSpan);
        Object.assign(inputEl.style, {
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          margin: 0,
          padding: cs.padding,
          font: cs.font,
          lineHeight: cs.lineHeight,
          letterSpacing: cs.letterSpacing,
          background: "transparent",
          border: "none",
          boxSizing: "border-box",
          zIndex: 2,
          outline: "none",
          width: "auto",
        });

        textSpan.style.visibility = "hidden";
        dom.appendChild(inputEl);
        inputEl.focus();
        inputEl.select();

        // initial size
        inputEl.style.width = `${textSpan.scrollWidth}px`;

        // resize on input
        inputEl.addEventListener("input", () => {
          textSpan.textContent = inputEl.value;
          inputEl.style.width = `${textSpan.scrollWidth}px`;
        });

        // key handling
        inputEl.addEventListener("keydown", (evt) => {
          // Ctrl/Cmd+A
          const isSelectAll =
            evt.key.toLowerCase() === "a" && (evt.ctrlKey || evt.metaKey);
          if (isSelectAll) {
            evt.preventDefault();
            inputEl.select();
            return;
          }
          // commit
          if (evt.key === "Enter" || evt.key === "Tab") {
            evt.preventDefault();
            inputEl.blur();
          }
          // cancel
          else if (evt.key === "Escape") {
            evt.preventDefault();
            inputEl.value = currentNode.attrs.label;
            inputEl.blur();
          }
        });

        // blur → save
        inputEl.addEventListener("blur", () => {
          if (inputEl) saveAndExit(inputEl.value);
        });
      };

      // 5) dblclick → edit
      dom.addEventListener("dblclick", (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        startEditing();
      });

      // Add touch event handlers for mobile
      let touchTimeout;
      let touchStartTime;

      dom.addEventListener("touchstart", (evt) => {
        touchStartTime = Date.now();
        touchTimeout = setTimeout(() => {
          evt.preventDefault();
          evt.stopPropagation();
          startEditing();
        }, 500); // 500ms for long press
      });

      dom.addEventListener("touchend", () => {
        clearTimeout(touchTimeout);
      });

      dom.addEventListener("touchmove", () => {
        clearTimeout(touchTimeout);
      });

      return {
        dom,
        stopEvent: () => isEditing,
        ignoreMutation: () => true,

        update(newNode) {
          if (newNode.type !== currentNode.type) return false;
          currentNode = newNode;

          // refresh data-attrs & label
          Object.entries(currentNode.attrs).forEach(([k, v]) => {
            dom.setAttribute(`data-${k}`, String(v ?? ""));
          });
          textSpan.textContent = currentNode.attrs.label;

          // typing animation
          const currentPos = editor.state.selection.from.toString();
          if (currentNode.attrs.position === currentPos) {
            textSpan.classList.add("typing");
          } else {
            textSpan.classList.remove("typing");
          }

          return true;
        },
      };
    };
  },
});
